import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconStarFilled from '@adv-mt/mt-svg-icons/lib/StarFilled.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Icon, ICON_SIZES} from '@adv-mt/ui/icon'
import {Subhead1} from '@adv-mt/ui/typography'

import DealerRatingsReview from '../../../components/dealerRatingsReview/DealerRatingsReview.js'
import {BASE_CLASS as PAGE_BASE_CLASS} from '../../../config.js'
import {DRAWER_TYPES} from '../../../drawers/config.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-dealerRatings`

const PREVIEW_REVIEWS_COUNT = 2

// Best rated first, and among equally rated ones the most recent
const byBestAndNewest = (a, b) => b.score - a.score || new Date(b.givenAt) - new Date(a.givenAt)

const DealerRatings = ({ad, onOpenDrawer, pageName, ratings, reviews = []}) => {
  const {domain, i18n} = useSuiContext()
  const {track: analyticsTrack} = useAnalyticsTrack()

  const config = domain.get('config')
  const {HIT_ORIGINS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !config.get('isMobile')
  })

  if (!ratings?.average || reviews.length === 0) return null

  // The use case returns them newest first — the panel only previews the best rated ones, while the
  // drawer keeps the chronological order
  const previewReviews = [...reviews].sort(byBestAndNewest).slice(0, PREVIEW_REVIEWS_COUNT)

  const handleShowAllRatings = async () => {
    onOpenDrawer({data: {ad, pageName, ratings, reviews}, type: DRAWER_TYPES.DEALER_RATINGS})

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.RATINGS_CLICKED,
      origin: HIT_ORIGINS.RATING_DETAIL
    })

    analyticsTrack(eventData)
  }

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-title`}>
        <Subhead1 as="h3">{i18n.t('DEALER_RATINGS.PANEL_TITLE', {average: ratings.average})}</Subhead1>

        <Icon size={ICON_SIZES.SM} className={`${BASE_CLASS}-title-icon`}>
          <IconStarFilled size={ICON_SIZES.SM} />
        </Icon>
      </div>

      <div className={`${BASE_CLASS}-reviews`}>
        {previewReviews.map(review => (
          <DealerRatingsReview key={review.feedbackId} review={review} />
        ))}
      </div>

      {reviews.length > 2 && (
        <Button
          fullWidth={!isHigherThanMobile}
          onClick={handleShowAllRatings}
          shape={BUTTON_SHAPES.CIRCLE}
          size={isHigherThanMobile ? BUTTON_SIZES.MEDIUM : BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.OUTLINE}
        >
          {i18n.t('DEALER_RATINGS.SHOW_ALL', {count: ratings.totalReviews})}
        </Button>
      )}
    </div>
  )
}

DealerRatings.displayName = 'DealerRatings'

DealerRatings.propTypes = {
  ad: PropTypes.object,
  onOpenDrawer: PropTypes.func.isRequired,
  pageName: PropTypes.string,
  ratings: PropTypes.shape({
    average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tags: PropTypes.arrayOf(PropTypes.string),
    totalReviews: PropTypes.number
  }),
  reviews: PropTypes.arrayOf(PropTypes.object)
}

export default DealerRatings
